<template>
  <v-app
    @contextmenu="context_menu_handler($event)"
    :theme="state.theme"
    style="background: var(--main-bg-color); overflow: hidden; height: 100%; max-height: 100dvh;"
    id="app">
    <!-- iOS Browser Mode Warning - encourages PWA installation -->
    <div v-if="showIOSBrowserWarning" class="ios-pwa-warning">
      <div class="ios-pwa-warning-content">
        <!-- Product Logo -->
        <div class="ios-pwa-warning-logo">
          <img
            v-if="deluge.canvaspro_core_info?.product_name?.toUpperCase() === 'OMNI'"
            :src="omniSvg"
            alt="CanvasPro Omni" />
          <img
            v-if="deluge.canvaspro_core_info?.product_name?.toUpperCase() === 'ONE'"
            :src="oneSvg"
            alt="CanvasPro One" />
          <img
            v-if="deluge.canvaspro_core_info?.product_name?.toUpperCase() === 'ORB'"
            :src="orbSvg"
            alt="CanvasPro Orb" />
        </div>
        <p v-if="state.machine_name" class="ios-pwa-warning-hostname">{{ state.machine_name }}</p>
        <h2 class="ios-pwa-warning-title">
          <v-icon size="28" color="white" class="mr-2">mdi-certificate</v-icon>
          Step 1: Install Certificate
        </h2>
        <p class="ios-pwa-warning-text">Install the certificate first so the home screen icon generates correctly.</p>
        <ol class="ios-pwa-warning-steps">
          <li><a href="/canvaspro.crt" class="ios-pwa-warning-cert-link">Download certificate</a> and tap <strong>Allow</strong></li>
          <li><strong>Settings → Profile Downloaded → Install</strong></li>
          <li><strong>Settings → General → About → Certificate Trust Settings</strong> → enable CanvasPro</li>
        </ol>
        <h2 class="ios-pwa-warning-title">
          <v-icon size="28" color="white" class="mr-2">mdi-export-variant</v-icon>
          Step 2: Add to Home Screen
        </h2>
        <p class="ios-pwa-warning-text">
          For the best experience, add CanvasPro to your home screen:
        </p>
        <ol class="ios-pwa-warning-steps">
          <li>Tap the <strong>Share</strong> button <v-icon size="18" color="white">mdi-export-variant</v-icon> in your browser toolbar</li>
          <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
          <li>Tap <strong>"Add"</strong> in the top right corner</li>
          <li>Open CanvasPro from your home screen</li>
        </ol>
        <div class="ios-pwa-warning-bypass">
          <span
            class="ios-pwa-warning-bypass-link"
            @click="dismissIOSBrowserWarning">
            Continue in browser anyway
          </span>
        </div>
      </div>
    </div>
    <!-- iOS PWA Status Bar Player Name -->
    <div v-if="isIOSStandalone && state.machine_name" class="ios-status-bar-name">
      {{ state.machine_name }}
    </div>
    <!-- Streamer hits the on-prem go2rtc service at /api/go2rtc/*. In cloud
         mode that service doesn't exist on the edge — preview relay through
         the player WS will replace it. Skip the mount entirely until then. -->
    <Streamer
      v-if="!IS_CLOUD_MODE"
      :output="0"
      :streamName="orbStreamName" />
    <AssetsTemplatesDropdownOverlay v-if="state.open" />
    <v-dialog
      v-model="userConsentDialog"
      max-width="600"
      v-if="!accepteulaopen && !first_time_setup">
      <TelemetryDialog
        v-if="userConsentDialog"
        v-on:close="userConsentDialog = false" />
    </v-dialog>
    <div
      ref="popup"
      class="popup"
      :style="popupStyle">
      <div
        ref="content"
        class="content">
        <v-slide-y-transition>
          <ConditionPicker
            v-if="
              delayConditionSummary &&
              localConditionSummary &&
              state.channelOnAirEditMode &&
              !state.cueTakeUI.sceneDragActive
            "
            :summaryMode="true"
            :item="localConditionSummary"
            itemtype="scene" />
        </v-slide-y-transition>
      </div>
    </div>
    <div
      v-if="isReconnecting || loading"
      class="connecting-container">
      <div>
        <div
          style="
            user-select: none;
            color: White;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
          Connecting to CanvasPro...
        </div>
        <div
          style="display: flex; justify-content: center; align-items: center">
          <v-progress-circular
            class="my-5"
            :size="100"
            :width="10"
            color="primary"
            indeterminate></v-progress-circular>
        </div>
      </div>
    </div>
    <div v-else-if="!loading">
      <transition name="fade">
        <accept-eula
          v-if="accepteulaopen"
          v-on:close="accepteulaopen = false" />
      </transition>

      <transition name="fade">
        <first-time-setup
          @respObj="(respObj) => (auth_token = respObj.access_token)"
          v-on:complete="logged_in_handler"
          v-if="first_time_setup" />
      </transition>

      <transition name="slide-fade2">
        <Login
          v-if="
            !first_time_setup &&
            logged_in === false &&
            !forgot_password &&
            !forgot_password2 &&
            route?.name !== 'ForgotPassword'
          "
          v-on:success="logged_in_handler"
          v-on:close="handleLogout(1)" />

        <div
          class="ma-5"
          style="
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          "
          v-else-if="
            logged_in === false && forgot_password && !forgot_password2
          ">
          <ResetPassword />
        </div>
      </transition>
      <div v-if="!deluge_offline && logged_in">
        <Upload />
        <Console v-if="state.console_open" />

        <BottomBar />

        <div
          id="top-bar"
          :style="tabhover ? 'left: 80px' : 'left: 80px'"
          style="
            width: calc(100vw - 80px);
            height: 50px;
            display: none;
            position: absolute;
            justify-content: space-between;
            align-items: center;
            user-select: none;
          ">
          <v-icon
            @click="phonetabs = true"
            dark
            style="opacity: 1; color: white; margin: 10px">
            mdi-menu
          </v-icon>
          <div
            :style="tabhover ? 'margin-left: 100px' : 'margin-left: 0px'"
            style="
              flex-direction: column;
              justify-content: center;
              display: flex;
              transition: all 0.2s ease-in;
              align-items: end;
              margin-right: 10px;
            "
            v-if="deluge">
            <div
              class="CanvasProFont CurrentMachine"
              style="
                font-weight: bolder;
                color: white !important;
                user-select: none;
              "
              v-if="deluge.config">
              <div
                v-if="
                  deluge_main['canvaspro_core_info'][
                    'product_name'
                  ].toLowerCase() === 'omni'
                ">
                <div
                  v-if="
                    selected_player &&
                    deluge?.config?.name !== deluge_main?.config?.name
                  "
                  style="display: flex; align-items: center">
                  <div
                    class="HomeLogo HomeLogoHover"
                    style="opacity: 0.5"
                    @click="switch_back()">
                    {{ deluge_main?.config.name }}
                  </div>
                  <v-icon
                    dark
                    style="opacity: 0.5">
                    mdi-chevron-right
                  </v-icon>
                  {{ deluge?.config?.name }}
                </div>
                <div
                  v-else
                  class="HomeLogo HomeLogoHover">
                  {{ deluge?.config?.name }}
                </div>
              </div>
              <div v-else>
                <div class="HomeLogo">{{ deluge?.config?.name }}</div>
              </div>
            </div>
          </div>
        </div>

        <div
          style="user-select: none"
          id="left-bar"
          class="tabs-container">
          <div
            @mouseover="hoverzOver()"
            @mouseout="hoverzOut()"
            class="tabicon-logo"
            :style="tabhover ? 'width: 220px' : ''"
            style="transform: scale(1); cursor: pointer">
            <div
              style="
                display: flex;
                width: 100%;
                padding-left: 10px;
                padding-right: 8px;
                align-content: center;
                /* justify-content: center; */
                color: white !important;
                height: 65%;
              ">
              <img
                v-if="deluge_main['canvaspro_core_info']?.['product_name']?.toUpperCase() === 'ORB'"
                :src="orbSvg"
                style="transform: scale(1); height: 100%" />
              <img
                v-else-if="deluge_main['canvaspro_core_info']?.['product_name']?.toUpperCase() === 'OMNI'"
                :src="omniSvg"
                style="transform: scale(1); height: 100%" />
              <img
                v-else
                :src="oneSvg"
                style="transform: scale(1); height: 100%" />
              <div
                v-if="tabhover"
                class="tab-name"
                style="
                  color: white;
                  margin-left: 8px;
                  margin-right: 5px;
                  display: flex;
                  align-items: flex-start;
                  flex-direction: column;
                  margin-top: -6px;
                  overflow: hidden;
                  position: relative;
                ">
                <p
                  style="
                    padding-left: 6px;
                    font-size: 17px;
                    white-space: nowrap;
                    text-transform: capitalize;
                  ">
                  CanvasPro
                  {{ deluge_main['canvaspro_core_info']['product_name'] }}
                </p>
                <div style="width: 143px">
                  <div
                    ref="playerHeader"
                    class="HomeLogoTitle-outer"
                    v-if="
                      selected_player &&
                      deluge?.config?.name !== deluge_main?.config.name
                    "
                    style="display: flex; align-items: center">
                    <div
                      ref="playerHeaderInner"
                      class="HomeLogo HomeLogoTitle"
                      style="opacity: 0.5"
                      @click="switch_back()">
                      {{ deluge_main?.config.name }}
                    </div>
                    <v-icon
                      dark
                      style="opacity: 0.5">
                      mdi-chevron-right
                    </v-icon>
                    {{ deluge?.config?.name }}
                  </div>
                  <div
                    v-else
                    ref="playerHeader"
                    @click="router.push({ name: 'Players' }).catch(() => {})"
                    class="HomeLogoTitle-outer">
                    <div
                      ref="playerHeaderInner"
                      class="HomeLogo HomeLogoTitle">
                      {{ deluge?.config?.name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <v-divider
            v-if="tabs.length > 0"
            :style="`margin-left: 18px; width: ${tabhover ? 190 : 50}px`"
            class="px-4" />
          <div class="tabs-inner-scroll">
            <div
              class="CanvasProFont"
              v-for="(tab, i) in tabs"
              :key="i"
              style="cursor: pointer"
              :title="tab.name">
              <div
                @click="
                  () =>
                    tab.name === 'Templates'
                      ? handleTemplatesFunc(tab)
                      : handleOnAirFunc(tab)
                "
                class="tabicon"
                :style="tabhover ? 'width: 195px' : ''"
                :class="
                  route.name === tab.route || currentRouteIsSub(tab, route) || (tab.route === 'Channels' && route.name === 'ChannelDetail')
                    ? 'tabiconsel'
                    : ''
                ">
                <div
                  style="
                    display: flex;
                    width: 100%;
                    padding-left: 12px;
                    padding-right: 8px;
                    align-content: center;
                    color: white !important;
                  ">
                  <v-icon
                    :icon="tab.icon"
                    color="white"
                    size="x-large" />
                  <div
                    v-if="tabhover"
                    class="tab-name"
                    style="
                      color: white;
                      margin-left: 17px;
                      margin-right: 5px;
                      display: flex;
                      align-items: center;
                    ">
                    {{ tab.name }}
                  </div>
                </div>

                <!-- Sub-items for Templates and OnAir tabs -->
                <div
                  v-if="
                    (tab.name === 'Templates' && route.name === tab.route) ||
                    currentRouteIsSub(tab, route) ||
                    (tab.name === 'OnAir' && route.name === tab.route)
                  "
                  class="nav-sub-items"
                  :style="tabhover ? 'margin-top: 8px;' : 'margin-top: 12px;'">
                  <ul :style="tabhover ? '' : 'margin-left: 0px;'">
                    <li
                      :style="
                        !tabhover
                          ? 'padding: 20px 0px; justify-content: center; '
                          : ' padding: 10px 20px 10px 20px; display: flex; justify-content: space-between;'
                      "
                      @click.stop="
                        handleSubButtonClick(
                          tab.name === 'Templates' ? 'Templates' : 'OnAir'
                        )
                      "
                      :class="
                        lastClicked !==
                        (tab.name === 'Templates' ? 'Presets' : 'OnAirControl')
                          ? 'nav-sub-item-active'
                          : ''
                      ">
                      <v-icon>
                        {{
                          tab.name === 'Templates'
                            ? 'mdi-file-document'
                            : 'mdi-layers-triple'
                        }}
                      </v-icon>
                      <a :style="tabhover ? '' : 'display: none;'">
                        {{ tab.name === 'Templates' ? 'Files' : 'Frames' }}
                      </a>
                    </li>
                    <li
                      :style="
                        !tabhover
                          ? 'padding: 20px 0px; justify-content: center; '
                          : ' padding: 10px 20px 10px 20px; display: flex; justify-content: space-between;'
                      "
                      @click.stop="
                        handleSubButtonClick(
                          tab.name === 'Templates' ? 'Presets' : 'OnAirControl'
                        )
                      "
                      :class="
                        lastClicked ===
                        (tab.name === 'Templates' ? 'Presets' : 'OnAirControl')
                          ? 'nav-sub-item-active'
                          : ''
                      ">
                      <v-icon>
                        {{
                          tab.name === 'Templates'
                            ? 'mdi-tune-variant'
                            : 'mdi-grid'
                        }}
                      </v-icon>
                      <a :style="tabhover ? '' : 'display: none;'">
                        {{ tab.name === 'Templates' ? 'Presets' : 'Grid' }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <v-divider
            :style="`margin-left: 18px; width: ${tabhover ? 190 : 50}px`"
            class="px-4" />
          <div
            @click="
              router.push({ path: '/Apps/' + app.api_name }).catch((err) => {})
            "
            class="CanvasProFont"
            style="cursor: pointer"
            v-for="app in deluge.apps"
            :key="app.api_name">
            <div
              class="tabicon"
              :style="tabhover ? 'width: 195px' : ''"
              :class="route.params.appname == app.api_name ? 'tabiconsel' : ''">
              <div
                style="
                  display: flex;
                  width: 100%;
                  padding-left: 12px;
                  padding-right: 8px;
                  align-content: center;
                  /* justify-content: center; */
                  color: white !important;
                ">
                <v-icon
                  color="white"
                  size="x-large"
                  :title="app.display_name">
                  {{ app.icon }}
                </v-icon>
                <div
                  v-if="tabhover"
                  class="tab-name"
                  style="
                    color: white;
                    margin-left: 17px;
                    margin-right: 5px;
                    display: flex;
                    align-items: center;
                    /* justify-content: center; */
                    white-space: nowrap;
                    width: 80px;
                    text-overflow: ellipsis;
                  ">
                  {{ app.display_name }}
                </div>
              </div>
            </div>
          </div>
          <div
            class="CanvasProFont"
            style="cursor: pointer; opacity: 0.8">
            <div
              class="tabicon"
              :style="tabhover ? 'width: 195px' : ''"
              @click="open_file_dialog()">
              <input
                accept=".capp"
                type="file"
                style="display: none"
                multiple
                id="app_file_upload"
                @input="send_to_upload_queue()" />
              <div
                style="
                  display: flex;
                  width: 100%;
                  padding-left: 12px;
                  padding-right: 8px;
                  align-content: center;
                  /* justify-content: center; */
                  color: white !important;
                ">
                <v-icon
                  color="white"
                  size="x-large"
                  title="Add App">
                  mdi-plus
                </v-icon>
                <div
                  v-if="tabhover"
                  class="tab-name"
                  style="
                    color: white;
                    margin-left: 17px;
                    margin-right: 5px;
                    display: flex;
                    align-items: center;
                    /* justify-content: center; */
                    white-space: nowrap;
                    width: 80px;
                    text-overflow: ellipsis;
                  ">
                  Add App
                </div>
              </div>
            </div>
          </div>
          <div
            class="expand-tabs"
            :class="tabhover ? 'expand-tabs-active' : 'expand-tabs-inactive'"
            @click="collapseBtnClick()">
            <div class="expand-tabs-child">
              <v-icon style="margin-left: 10px; font-size: 30px">
                mdi-chevron-double-right
              </v-icon>
              <p class="CanvasProFont">Collapse</p>
            </div>
          </div>
        </div>
        <transition name="slide-fade2">
          <div
            v-if="phonetabs"
            class="phone-tabs-overlay">
            <div
              class="phone-tabs-clickarea"
              @click="phonetabs = false"></div>
          </div>
        </transition>
        <div
          id="left-bar2"
          style="user-select: none"
          :style="
            phonetabs
              ? 'transform: translateX(0px);'
              : 'box-shadow: unset !important;'
          "
          class="tabs-container">
          <div
            id="logolarge"
            v-if="state.is_orb">
            <img
              :src="orbSvg"
              style="height: 60%" />
          </div>
          <div
            id="logolarge"
            v-else-if="state.is_omni">
            <img
              :src="omniSvg"
              style="height: 60%" />
          </div>
          <div
            id="logolarge"
            v-else>
            <img
              :src="omniSvg"
              style="height: 60%" />
          </div>

          <v-divider
            class="tabs-divider"
            style="
              color: rgb(255, 255, 255);
              caret-color: rgb(255, 255, 255);
              margin-left: 10px;
              margin-right: 10px;
              margin-top: 0px;
            "
            color="#fff" />

          <div
            class="CanvasProFont"
            v-for="(tab, i) in tabs"
            :key="i"
            @click="
              (router.push({ name: tab.route }).catch((err) => {}),
              (phonetabs = false))
            "
            style="cursor: pointer">
            <div
              class="tabicon"
              :class="route.name == tab.route || (tab.route === 'Channels' && route.name === 'ChannelDetail') ? 'tabiconsel' : ''">
              <div
                style="
                  display: flex;
                  width: 100%;
                  padding-left: 8px;
                  padding-right: 8px;
                  align-content: center;
                  justify-content: center;
                  color: white !important;
                ">
                <v-icon
                  dark
                  x-large>
                  {{ tab.icon }}
                </v-icon>
                <div
                  v-if="tabhover"
                  style="
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "></div>
              </div>
            </div>
          </div>
          <v-divider
            style="
              margin-left: 10px;
              margin-right: 10px;
              margin-top: 15px;
              color: #fff;
            "
            dark
            color="#fff" />
          <div
            class="CanvasProFont"
            style="cursor: pointer"
            v-for="app in deluge.apps"
            :key="app.api_name">
            <div
              class="tabicon"
              @click="
                (router
                  .push({ path: '/Apps/' + app.api_name })
                  .catch((err) => {}),
                settab())
              "
              :class="route.params.appname == app.api_name ? 'tabiconsel' : ''">
              <div
                style="
                  display: flex;
                  width: 100%;
                  padding-left: 8px;
                  padding-right: 8px;
                  align-content: center;
                  justify-content: center;
                  color: white !important;
                ">
                <v-icon
                  color="white"
                  size="x-large">
                  {{ app.icon }}
                </v-icon>
                <div
                  v-if="tabhover"
                  style="
                    color: white;
                    margin-left: 17px;
                    margin-right: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "></div>
              </div>
            </div>
          </div>
          <div
            @click="router.push({ name: tab.route }).catch((err) => {})"
            style="cursor: pointer">
            <div
              class="tabicon"
              style="color: white !important"
              :class="route.name == tab.route ? 'tabiconsel' : ''">
              <v-icon
                style="opacity: 0.9"
                dark
                x-large>
                mdi-plus
              </v-icon>
            </div>
          </div>
        </div>
        <div
          :style="
            tabhover
              ? 'transition: all 0.3s; transform: translateX(100px); width: calc(100vw - 200px); margin-left: 150px;'
              : 'transition: all 0.3s;'
          "
          style="
            background: var(--v-backgroundColorDarker-base);
            right: 0px;
          "
          :class="open_conolse ? 'appmainconsole' : 'appmain'">
          <router-view v-slot="{ Component, route }">
            <transition name="slide-fade">
              <component
                v-on:player_select="player_select"
                v-on:player_switch="switch_player"
                v-if="
                  logged_in ||
                  (route.name === 'ForgotPassword' &&
                    !deluge_offline &&
                    logged_in)
                "
                :key="route.path"
                :is="Component" />
            </transition>
          </router-view>
        </div>
      </div>
    </div>
  </v-app>
</template>
<script setup>
import FirstTimeSetup from './components/Account/FirstTimeSetup.vue';
import StartupSettings from './components/Configure/VentuzConfig/StartupSettings.vue';
import Console from './components/Shared/Console.vue';
import ResetPassword from './components/Shared/ResetPassword.vue';
import Login from './components/Account/Login.vue';
import BottomBar from './components/Shared/BottomBar.vue';
import {
  onMounted,
  ref,
  onBeforeMount,
  onBeforeUnmount,
  watch,
  computed,
} from 'vue';
import Streamer from './Streamer.vue';
import { useRouter, useRoute } from 'vue-router';
import { deluge } from '@/stores/deluge.js';
import { state, deluge_sock, IS_CLOUD_MODE } from '@/stores/deluge.js';
import { $http } from './stores/http.js';
import omniSvg from './assets/OmniLight.svg';
import oneSvg from './assets/OneLight.svg';
import orbSvg from './assets/CanvasPro_Orb_Anagram_White_7680px.svg';
import Upload from './Upload.vue';
import AcceptEula from './components/Account/AcceptEula.vue';
import { applyPatch } from './utils/JSONpatch.js';
import ConditionPicker from './components/Shared/ConditionPicker.vue';
import serverTime from './serverTime';
import TelemetryDialog from './components/Telemetry/TelemetryDialog.vue';
import AssetsTemplatesDropdownOverlay from './AssetsTemplatesDropdownOverlay.vue';

const router = useRouter();
const route = useRoute();
const deluge_main = ref({});
const tab = ref(0);
const open_conolse = ref(false);
const tabhover = ref(true);
const userConsentDialog = ref(false);
const deluge_offline = computed(() => {
  if (!deluge_sock.value) {
    return true;
  }
  return deluge_sock.value.readyState !== WebSocket.OPEN;
});
const loading = ref(true);
const logged_in = ref(false);

// iOS browser warning - show if on iOS but not in standalone PWA mode
const showIOSBrowserWarning = ref(false);


// Track iOS standalone mode for status bar player name
const isIOSStandalone = ref(false);

// Dynamic stream name for Orb mode based on selected frame
const orbStreamName = computed(() => {
  if (!state.is_orb) {
    return 'preview';
  }
  // Frame 1 uses 'orb', Frame 2 uses 'orb2', etc.
  const frameNo = state.selected_frame || 1;
  return frameNo === 1 ? 'orb' : `orb${frameNo}`;
});

const isReconnecting = ref(false);
let reconnectAttempts = 0;
let reconnectTimer = null;

function attemptConnection() {
  if (
    deluge_sock.value &&
    deluge_sock.value.readyState === WebSocket.CONNECTING
  ) {
    console.log('Connection attempt already in progress.');
    return;
  }

  clearConnectionTimers();
  isReconnecting.value = true;
  loading.value = true;

  TestLogin().then((isLoggedIn) => {
    if (!isLoggedIn) {
      loading.value = false;
      isReconnecting.value = false;
      logged_in.value = false;
      console.log('Not logged in, aborting connection attempt.');
      return;
    }
    logged_in.value = true;

    const connectionPromise = new Promise((resolve, reject) => {
      const connectionTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timed out after 10 seconds.'));
      }, 10000);

      if (deluge_sock.value) {
        deluge_sock.value.onopen = null;
        deluge_sock.value.onmessage = null;
        deluge_sock.value.onclose = null;
        deluge_sock.value.onerror = null;
        if (
          deluge_sock.value.readyState === WebSocket.OPEN ||
          deluge_sock.value.readyState === WebSocket.CONNECTING
        ) {
          deluge_sock.value.close();
        }
      }

      // Cloud mode hits the Worker on a different origin; on-prem composes
      // wss:// + window.location.host + state.devRedirect as before.
      const _delugeBase = state.wsBase
        ? state.wsBase
        : (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
          window.location.host +
          (typeof state.devRedirect === 'string' &&
          state.devRedirect.startsWith('/')
            ? state.devRedirect
            : '');
      deluge_sock.value = new WebSocket(
        _delugeBase +
          '/deluge_core?token=' +
          localStorage.getItem('authentication_token')
      );

      deluge_sock.value.onopen = () => {
        clearTimeout(connectionTimeout);
        resolve();
      };

      deluge_sock.value.onclose = (event) => {
        clearTimeout(connectionTimeout);
        reject(new Error(`WebSocket closed. Code: ${event.code}`));
      };

      deluge_sock.value.onerror = () => {
        clearTimeout(connectionTimeout);
        reject(new Error('WebSocket connection error.'));
      };
    });

    connectionPromise
      .then(() => {
        reconnectAttempts = 0;
        isReconnecting.value = false;
        setupWebSocketHandlers();
      })
      .catch((error) => {
        console.error(error.message);
        if (deluge_sock.value) {
          deluge_sock.value.close();
        }
        handleDisconnection();
      });
  });
}

function handleDisconnection() {
  console.log('Handling disconnection...');
  clearConnectionTimers();
  logged_in.value = false;
  isReconnecting.value = true;
  loading.value = true;

  reconnectAttempts++;
  const delay = Math.min(30000, Math.pow(2, reconnectAttempts) * 1000);

  console.log(`Will attempt to reconnect in ${delay / 1000} seconds.`);
  reconnectTimer = setTimeout(attemptConnection, delay);
}

function setupWebSocketHandlers() {
  deluge_sock.value.onmessage = (msg) => {
    if (loading.value) loading.value = false;
    if (isReconnecting.value) isReconnecting.value = false;

    const parsed_data = JSON.parse(msg.data);

    if (parsed_data.patch) {
      applyPatch(deluge, parsed_data.patch.data);
    }

    if (!selected_player.value) {
      Object.assign(deluge, parsed_data);
      deluge_main.value = deluge;
    } else {
      const data = parsed_data;
      if (Object.keys(data)[0] === selected_player.value.id) {
        Object.assign(deluge, Object.values(data)[0]);
      } else {
        deluge_main.value = { ...deluge_main.value, ...data };
      }
    }
    document.title = 'CanvasPro: ' + deluge?.config?.name;
  };

  deluge_sock.value.onclose = () => {
    console.error('WebSocket was closed.');
    handleDisconnection();
  };

  deluge_sock.value.onerror = () => {
    console.error('A WebSocket error occurred.');
  };

  deluge_sock.value.send(
    new Blob([
      JSON.stringify({
        cmd: 'GET',
        keys: [
          'apps',
          'templates',
          'player_resolution',
          'runtime_onair',
          'tags',
          'canvaspro_core_info',
          'channels',
          'assets',
          'all_conditions',
          'config',
          'info',
          'hw',
          'asset_conversion_status',
          'items',
          'ingest',
          'frames',
          'triggers',
          'players',
          'connected_players',
          'available_players',
          'temp_stat',
          'template_upgrade',
          'cs',
          'roles',
          'new_cued_dids',
          'feature_flags',
          'pinned_svars',
          'volume_svars',
          'ndi',
          'latest_log',
          'omni_connection',
          'omni_client_state',
          'panels_meta',
          'panels',
          'explorer_status',
        ],
      }),
    ])
  );
  deluge_sock.value.send(
    new Blob([
      JSON.stringify({
        cmd: 'SUBSCRIBE',
        keys: [
          'apps',
          'templates',
          'runtime_onair',
          'tags',
          'canvaspro_core_info',
          'channels',
          'assets',
          'asset_conversion_status',
          'patch',
          'all_conditions',
          'config',
          'hw',
          'info',
          'onair_scene_change_event',
          'items',
          'ingest',
          'frames',
          'players',
          'connected_players',
          'available_players',
          'temp_stat',
          'template_upgrade',
          'cs',
          'roles',
          'new_cued_dids',
          'feature_flags',
          'pinned_svars',
          'volume_svars',
          'ndi',
          'latest_log',
          'omni_connection',
          'omni_client_state',
          'panels_meta',
          'panels',
          'explorer_status',
        ],
      }),
    ])
  );
  serverTime.forceSync();
}

function clearConnectionTimers() {
  clearTimeout(reconnectTimer);
}

function onNetworkOnline() {
  console.log('Device is back online. Attempting to reconnect immediately.');
  reconnectAttempts = 0;
  clearTimeout(reconnectTimer);
  attemptConnection();
}
function onNetworkOffline() {
  console.log('Device is offline. Connection will be paused.');
  if (deluge_sock.value && deluge_sock.value.readyState === WebSocket.OPEN) {
    deluge_sock.value.close();
  } else {
    clearConnectionTimers();
  }
}

const count = ref(30);
const delayConditionSummary = ref(false);
const delayConditionSummaryTask = ref(false);
const delayConditionSummaryTask2 = ref(false);
const localConditionSummary = ref(null);
watch(
  () => state.sceneConditionSummary,
  (newVal, oldVal) => {
    if (newVal) {
      localConditionSummary.value = newVal;
      clearTimeout(delayConditionSummaryTask2.value);
      delayConditionSummaryTask.value = setTimeout(() => {
        delayConditionSummary.value = true;
      }, 600);
    } else {
      delayConditionSummaryTask2.value = setTimeout(() => {
        delayConditionSummary.value = false;
      }, 200);
      clearTimeout(delayConditionSummaryTask.value);
    }
  }
);

const forgot_password = ref(false);
const forgot_password2 = ref(false);
const first_time_setup = ref(false);
const phonetabs = ref(false);
const upload_mode = ref(undefined);
const src_uid = ref(undefined);
const selected_player = ref(undefined);
const auth_token = ref(undefined);
const accepteulaopen = ref(false);
var marquee = ref(false);
const popup = ref(null);
const content = ref(null);
const popupStyle = ref({});

const updatePopupPosition = (event) => {
  if (!state.sceneConditionSummary) return;

  // Guard against missing refs (race conditions during mount/unmount)
  if (!popup.value || !content.value) return;

  const popupRect = popup.value.getBoundingClientRect?.();
  const contentRect = content.value.getBoundingClientRect?.();
  if (!popupRect || !contentRect) return;

  const offsetX = 20;
  const offsetY = -20;

  let left = event.clientX + offsetX;
  let top = event.clientY + offsetY;

  if (left + contentRect.width > window.innerWidth) {
    left = window.innerWidth - contentRect.width;
  }
  if (top + contentRect.height > window.innerHeight) {
    top = window.innerHeight - contentRect.height;
  }

  if (left < 0) {
    left = 0;
  }
  if (top < 0) {
    top = 0;
  }

  popupStyle.value = {
    left: `${left}px`,
    top: `${top}px`,
  };
};
const lastClicked = ref(localStorage.getItem('lastClicked') || 'Templates');
function currentRouteIsSub(tab, route) {
  if (tab.routes == undefined) {
    return false;
  } else {
    for (const subTab of tab.routes) {
      if (route.name == subTab.routeSub) {
        return true;
      } else {
      }
    }
    return false;
  }
}

const tabs = computed(() => {
  var temp_tabs = [];
  let tabsUpdated = false;
  if (!state.current_user) {
    return temp_tabs;
  }
  if (!deluge.roles) {
    return temp_tabs;
  }

  if (
    deluge.roles[state.current_user.role_id].permissions.includes(
      'create_asset'
    )
  ) {
    temp_tabs.push({
      name: 'Assets',
      route: 'Assets',
      icon: 'mdi-folder-multiple-image',
    });
  }
  if (
    deluge.roles[state.current_user.role_id].permissions.includes(
      'create_template'
    )
  ) {
    temp_tabs.push({
      name: 'Templates',
      route: 'Templates',
      routes: [
        {
          name: 'Presets',
          routeSub: 'Presets',
          icon: 'mdi-tune-variant',
        },
      ],
      icon: 'mdi-cube-scan',
      subItems: true,
      parentTitle: 'Templates',
    });
  }
  if (
    deluge.roles[state.current_user.role_id].permissions.includes(
      'update_channel'
    )
  ) {
    temp_tabs.push({
      name: 'Channels',
      route: 'Channels',
      icon: 'mdi-play-speed',
    });
    if (!state.is_omni || state.is_orb) {
      temp_tabs.push({
        name: 'OnAir',
        route: 'OnAir',
        routes: [
          {
            name: 'OnAirControl',
            routeSub: 'OnAirControl',
            icon: 'mdi-access-point',
          },
        ],
        icon: 'mdi-access-point',
        subItems: true,
        parentTitle: 'OnAir',
      });
    }
  }
  if (
    deluge.roles[state.current_user.role_id].permissions.includes(
      'update_channel'
    )
  ) {
    if (state.is_omni && !state.is_orb) {
      temp_tabs.push({
        name: 'Players',
        route: 'Players',
        icon: 'mdi-billboard',
      });
      temp_tabs.push({
        name: 'Live State',
        route: 'LiveState',
        icon: 'mdi-broadcast',
      });
    }
  }
  if (
    deluge.roles[state.current_user.role_id].permissions.includes(
      'update_config'
    )
  ) {
    temp_tabs.push({
      name: 'Configure',
      route: 'Configure',
      icon: 'mdi-cog',
    });
  }
  tabsUpdated = true;
  if (tabsUpdated) {
    if (
      !temp_tabs.map((tab) => tab.route).includes(route.name) &&
      route.name !== 'ChannelDetail' &&
      route.name !== 'Login' &&
      route.name !== 'ForgotPassword' &&
      route.name !== 'ResetPassword' &&
      route.name !== 'AcceptEula' &&
      !route?.name?.includes('App') &&
      state?.deluge?.feature_flags
    ) {
      router.push({ name: 'Assets' }).catch(() => {});
    }
  }

  if (
    !temp_tabs.length &&
    deluge.apps &&
    Object.values(deluge.apps).length > 0 &&
    !route?.name?.includes('App')
  ) {
    router
      .push({ path: '/Apps/' + Object.values(deluge.apps)[0].api_name })
      .catch(() => {});
  }
  return temp_tabs;
});

function handleSubButtonClick(subButtonName) {
  router.push({ name: subButtonName }).catch(() => {});
  lastClicked.value = subButtonName;
  localStorage.setItem('lastClicked', subButtonName);
}

function handleTemplatesFunc(tab) {
  if (tab.name === 'Templates') {
    if (lastClicked.value === 'Presets') {
      router.push({
        name: 'Presets',
      });
      return;
    }
  }
  router.push({ name: tab.route }).catch((err) => {});
}

function handleOnAirFunc(tab) {
  if (tab.name === 'OnAir') {
    if (lastClicked.value === 'OnAirControl') {
      router.push({
        name: 'OnAirControl',
      });
      return;
    }
  }
  router.push({ name: tab.route }).catch((err) => {});
}

onBeforeUnmount(() => {
  state.$ident = '';
  sessionStorage.setItem('ident', '');
  clearConnectionTimers();
  if (deluge_sock.value) {
    deluge_sock.value.close();
  }
  window.removeEventListener('online', onNetworkOnline);
  window.removeEventListener('offline', onNetworkOffline);
  window.removeEventListener('resize', setViewportHeightDebounced);
  document.removeEventListener('focusout', handleFocusOut);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', setViewportHeightDebounced);
  }
});
function collapseBtnClick() {
  tabhover.value = !tabhover.value;
  localStorage.setItem('nav_expanded', tabhover.value);
}
function isAnyDialogOpen() {
  return !!document.querySelector('.v-overlay--active');
}
function routeIsOnair() {
  return route.name === 'OnAir';
}
onBeforeMount(() => {
  if (localStorage.getItem('nav_expanded')) {
    tabhover.value = localStorage.getItem('nav_expanded') === 'true';
  } else {
    tabhover.value = true;
    localStorage.setItem('nav_expanded', true);
  }
  // Theme handling with user preference support (auto/dark/light)
  const themePreference = localStorage.getItem('themePreference') || 'auto';
  applyThemeFromPreference(themePreference);

  // Listen for system theme changes (only affects 'auto' mode)
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      const currentPref = localStorage.getItem('themePreference') || 'auto';
      if (currentPref === 'auto') {
        applyThemeFromPreference('auto');
      }
    });

  function applyThemeFromPreference(preference) {
    let theme;
    if (preference === 'auto') {
      const systemDark =
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      theme = systemDark ? 'dark' : 'light';
    } else {
      theme = preference;
    }
    localStorage.setItem('theme', theme);
    state.theme = theme;
    document.documentElement.setAttribute('data-theme', theme);
  }
  window.addEventListener('keydown', (e) => {
    // Defensive guards: some environments may emit events without a key
    const key = e && e.key ? e.key : undefined;
    if (key === 'Control') {
      state.ctrl_down = true;
    }
    if (key === 'Shift') {
      state.shift_down = true;
    }

    // normalize safely before calling toLowerCase()
    if (
      (key || '').toLowerCase() === 'e' &&
      ((!isAnyDialogOpen() && routeIsOnair()) || route.name === 'Channels' || route.name === 'ChannelDetail')
    ) {
      state.channelOnAirEditMode = !state.channelOnAirEditMode;
    }
  });
  window.addEventListener('keyup', (e) => {
    if (e.key == 'Control') {
      state.ctrl_down = false;
    }
    if (e.key == 'Shift') {
      state.shift_down = false;
    }
  });
  state.$ident = '';
  sessionStorage.setItem('ident', '');

  get_details();
});
// Detect iOS/iPad
function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

// Detect if running as standalone PWA
function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Add platform classes to body for CSS targeting
function setPlatformClasses() {
  if (isIOS()) {
    document.body.classList.add('ios-device');
    if (isStandalone()) {
      document.body.classList.add('ios-standalone');
      isIOSStandalone.value = true;
    } else {
      document.body.classList.add('ios-browser');
      // Always show warning for iOS browser mode
      showIOSBrowserWarning.value = true;
    }
  }
}

// Dismiss the iOS browser warning (not persisted - shows every time)
function dismissIOSBrowserWarning() {
  showIOSBrowserWarning.value = false;
}

// Set CSS variable for true viewport height (fixes iOS Chrome issues)
// Uses visualViewport when available as it's more reliable on iOS
function setViewportHeight() {
  // Prefer visualViewport.height as it's more accurate on iOS
  const vh = window.visualViewport
    ? window.visualViewport.height * 0.01
    : window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

// Debounced version for events that fire rapidly
let viewportHeightTimeout = null;
function setViewportHeightDebounced() {
  clearTimeout(viewportHeightTimeout);
  viewportHeightTimeout = setTimeout(setViewportHeight, 100);
}

// Force iOS to hide the bottom bar by scrolling
// iOS reserves space at the bottom for keyboard/URL bar - scrolling hides it
function forceIOSScrollFix() {
  if (!isIOS()) return;

  // Technique: Scroll document slightly to trigger iOS to hide reserved space
  // This mimics user scrolling which hides the bottom gap
  window.scrollTo(0, 1);
  setTimeout(() => window.scrollTo(0, 0), 50);

  // Also set viewport height
  setViewportHeight();
}

// Force iOS to recalculate viewport by triggering a reflow
function forceIOSViewportRecalc() {
  // Force reflow by reading offsetHeight
  void document.body.offsetHeight;

  // Set viewport height
  setViewportHeight();

  // Try scroll fix
  forceIOSScrollFix();
}

// Handle keyboard dismiss on iOS - recalculate viewport when inputs lose focus
function handleFocusOut(e) {
  // Only handle focus out from inputs/textareas (keyboard dismiss)
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
    // Delay to let iOS keyboard animation complete, then force recalc
    setTimeout(forceIOSViewportRecalc, 300);
    setTimeout(forceIOSViewportRecalc, 600);
  }
}

onMounted(async () => {
  // Set platform classes for CSS targeting (iOS browser vs standalone)
  setPlatformClasses();

  // Set initial viewport height and update on resize/orientation change
  setViewportHeight();
  window.addEventListener('resize', setViewportHeightDebounced);
  window.addEventListener('orientationchange', () => {
    // Delay to let iOS settle after orientation change
    setTimeout(setViewportHeight, 100);
    setTimeout(forceIOSScrollFix, 200);
  });

  // Listen for keyboard dismiss (focusout from inputs)
  document.addEventListener('focusout', handleFocusOut);

  // Use visualViewport API if available (more reliable on iOS)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', setViewportHeightDebounced);
  }

  // iOS: Force scroll fix on load to hide reserved keyboard space
  if (isIOS()) {
    setTimeout(forceIOSScrollFix, 100);
    setTimeout(forceIOSScrollFix, 500);
    setTimeout(forceIOSScrollFix, 1000);
  }

  window.addEventListener('mousemove', updatePopupPosition);
  if (window.innerWidth < 1250) {
    tabhover.value = false;
  }

  window.addEventListener('online', onNetworkOnline);
  window.addEventListener('offline', onNetworkOffline);
  attemptConnection();

  setInterval(update_token, 30000);

  window.addEventListener('resize', () => {
    if (tabhover.value == true && window.innerWidth < 950) {
      tabhover.value = false;
    }
  });
});
function send_to_upload_queue() {
  const file_input = document.getElementById('app_file_upload');
  for (const file of file_input.files) {
    state.upload_files.push(file);
  }
  file_input.files = new DataTransfer().files;
}
function open_file_dialog() {
  document.getElementById('app_file_upload').click();
}
async function logged_in_handler(respObj) {
  auth_token.value = respObj.access_token;
  logged_in.value = true;
  first_time_setup.value = false;
  attemptConnection();
  // Reset viewport height after login (keyboard may have changed it on iOS)
  // Use aggressive recalc to force iOS to update
  setTimeout(forceIOSViewportRecalc, 100);
  setTimeout(forceIOSViewportRecalc, 300);
  setTimeout(forceIOSViewportRecalc, 600);
  setTimeout(forceIOSViewportRecalc, 1000);
}
function player_select(data) {
  selected_player.value = data.player;
}
function switch_player(data) {
  router.push({ path: '/Assets' }).catch(() => {});
  selected_player.value = data.player;
  Object.assign(deluge, Object.values(JSON.parse(data.data))[0]);
  state.is_omni = false;
  state.$ident = '/omni/' + selected_player.value.id + '/';
  sessionStorage.setItem('ident', '/omni/' + selected_player.value.id + '/');
}
function AllowedFileTypes() {
  if (route.name === 'Templates') {
    file_type.value = '.vza';
  } else if (route.name === 'Assets') {
    file_type.value = '.mp4,.mov,.ts,.mkv,.avi,.png,.jpeg,.jpgm,.bmp';
  }
}
function get_details() {
  $http('GET', '/product_details').then((resp) => {
    let temp_dict = {};
    temp_dict['canvaspro_core_info'] = JSON.parse(resp);
    const pName =
      temp_dict['canvaspro_core_info']['product_name'].toLowerCase();
    console.log('[App] Product Name:', pName);

    // Check if product name indicates Orb
    let isOrbProduct = pName.includes('orb');

    // Also check if orb stream is available in go2rtc (for dev/testing)
    checkOrbStreamAvailable().then((orbStreamActive) => {
      state.is_orb = isOrbProduct || orbStreamActive;
      state.is_omni = pName === 'omni' || state.is_orb;
      console.log(
        '[App] is_omni:',
        state.is_omni,
        'is_orb:',
        state.is_orb,
        'orbStreamActive:',
        orbStreamActive
      );
    });

    Object.assign(deluge, temp_dict);
    state.machine_name = temp_dict['canvaspro_core_info']['name'];
    state.version = temp_dict['canvaspro_core_info']['version'];
    first_time_setup.value = !temp_dict.canvaspro_core_info.registered;
    state.telemetrySupport = temp_dict.canvaspro_core_info.telemetry;
    state.timezone_adjust = temp_dict.canvaspro_core_info.timezone_adjust;
  });
}

// Check if orb stream is active in go2rtc
async function checkOrbStreamAvailable() {
  // go2rtc lives in the on-prem stack only. In cloud mode preview will be
  // routed through the player WS once that's built; until then, report
  // "no stream" so the UI doesn't poll a non-existent service.
  if (IS_CLOUD_MODE) {
    return false;
  }
  try {
    const resp = await fetch('/api/go2rtc/streams');
    const streams = await resp.json();
    // Check if orb stream exists and has active producers with data
    if (streams.orb && streams.orb.producers) {
      const hasActiveProducer = streams.orb.producers.some(
        (p) => (p.format_name === 'rtsp' || p.format_name === 'mpjpeg') && p.bytes_recv > 0
      );
      console.log('[App] Orb stream check:', hasActiveProducer, streams.orb);
      return hasActiveProducer;
    }
    return false;
  } catch (e) {
    console.log('[App] Failed to check orb stream:', e);
    return false;
  }
}

async function switch_back() {
  sessionStorage.setItem('ident', '');
  state.$ident = '';
  var player_from = selected_player.value.id;
  selected_player.value = undefined;
  Object.assign(deluge, deluge_main.value);
  state.is_omni = true;
  router.push({ path: '/Players' }).catch(() => {});

  await $http(
    'PUT',
    '/deluge_core_websocket_unsub?uid=' +
      deluge.deluge_uuid +
      '&cpro_identity=' +
      player_from
  );
}

async function update_token() {
  if (!localStorage.getItem('authentication_token')) {
    console.log('No auth token!');
    return;
  }

  try {
    const response = await fetch(state.devRedirect + '/api/update_token', {
      method: 'PUT',
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('authentication_token'),
      },
    });

    if (response.status === 200) {
      const respObj = await response.json();
      if ('access_token' in respObj) {
        localStorage.setItem('authentication_token', respObj.access_token);
        auth_token.value = respObj.access_token;
      } else {
        localStorage.removeItem('authentication_token');
        if (deluge_sock.value) deluge_sock.value.close();
      }
    } else if (response.status === 401) {
      localStorage.removeItem('authentication_token');
      if (deluge_sock.value) deluge_sock.value.close();
    }
  } catch (e) {
    console.error('Token update failed:', e);
  }
}
function context_menu_handler(e) {
  if (import.meta.env.MODE === 'production') {
    e.preventDefault();
  }
}
async function TestLogin() {
  if (!localStorage.getItem('authentication_token')) {
    return false;
  }
  try {
    const response = await fetch(state.devRedirect + '/api/users/me', {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('authentication_token'),
      },
    });

    if (response.status === 200) {
      const user = await response.json();
      state.current_user = user;
      if (!user.eula_accepted) {
        accepteulaopen.value = true;
      }
      if (
        state.telemetrySupport &&
        user.extra_info?.user_consent === undefined
      ) {
        userConsentDialog.value = true;
        localStorage.setItem('userConsent', 'true');
      }
      return true;
    } else {
      localStorage.removeItem('authentication_token');
      return false;
    }
  } catch (error) {
    console.error('TestLogin failed:', error);
    return false;
  }
}
window.addEventListener('message', (event) => {
  if (
    typeof event.data === 'object' &&
    deluge_sock.value &&
    deluge_sock.value.readyState === WebSocket.OPEN
  ) {
    const data = event.data;
    if (data.deluge_cmd) {
      deluge_sock.value.send(new Blob([JSON.stringify(data.deluge_cmd)]));
    } else if (data.deluge_core_cmd) {
      deluge_sock.value.send(data.deluge_core_cmd);
    }
  }
});

const playerHeader = ref(null);
const playerHeaderInner = ref(null);

function hoverzOver() {
  var text1 = document.querySelector('.HomeLogoTitle');
  if (text1) {
    if (text1.innerHTML.length > 12) {
      marquee = true;
      if (playerHeader.value) {
        playerHeader.value.classList.add('marquee-outer-active');
      }
      if (playerHeaderInner.value) {
        playerHeaderInner.value.classList.add('marquee-inner-active');
      }
    }
  }
}

function hoverzOut() {
  marquee = false;
  if (playerHeader.value) {
    playerHeader.value.classList.remove('marquee-outer-active');
  }
  if (playerHeaderInner.value) {
    playerHeaderInner.value.classList.remove('marquee-inner-active');
  }
}
</script>

<style lang="scss">
@import './dialogs.css';
@import './components/Shared/pluginStyleOverrides.css';

// DONE

/* veutify class tweaks and fixes */
// .mx-3 {
//   padding-right: 12px !important;
//   padding-left: 12px !important;
//   margin-right: 0px !important;
//   margin-left: 0px !important;
// }
.v-window__container {
  height: 100% !important;
}

.v-color-picker-canvas {
  height: 220px !important;
}

.HomeLogo {
  color: white;
  background-color: #8b7f7f00;
  font-size: 20px;
  padding-left: 6px;
  padding-right: 6px;
  border-radius: 6px;
}

.HomeLogoTitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 135px;
  font-size: 14px;
  color: #9b9b9b;
  display: inline-block;
}

.HomeLogoHover {
  cursor: pointer;
  transition: background-color 0.2s ease-in;
}

.HomeLogoHover:hover {
  color: white;
  background-size: 0.2;
  background-color: var(--v-primary-base);
  box-shadow: 5px 5px 15px 5px rgba(0, 0, 0, 0.15) inset;
  opacity: 1 !important;
}

/* large dialog styling */

.template-pop-up > .v-overlay__content {
  max-width: 1200px !important;
  max-height: 1000px !important;
}

.template-pop-up > .v-overlay__content > .v-card,
.template-pop-up-fix .v-card {
  // height: calc(100dvh - 18px) !important;
  // max-height: 1000px !important;
}

.template-pop-up > .v-overlay__content > .v-card .px-3,
.template-pop-up > .v-overlay__content > .v-card .px-2 {
  height: 90%;
}

.template-pop-up > .v-overlay__content > .v-card .window-item,
.template-pop-up-fix .v-card .v-window-item {
  height: calc(100dvh - 208px) !important;
  max-height: 847px !important;
}

.template-pop-up-fix .modal-body > .v-window__container {
  overflow: auto;
}

.v-card-actions {
  min-height: unset !important;
}

.scrollable-nav .v-slide-group__container {
  overflow: unset;
}

.v-slide-group__container .v-tab {
  flex-grow: 1 !important;
}

.v-slide-group--vertical .v-btn__overlay {
  opacity: 0 !important;
}

.v-slide-group--vertical .v-ripple__container {
  display: none;
}

.v-slide-group--vertical .v-slide-group__container {
  contain: content;
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  background-color: var(--inner-background);
  border-radius: 8px;
  height: 100% !important;
  margin-left: 10px;
}

.v-slide-group--vertical .v-slide-group__content .v-btn--size-default {
  padding: 0 12px;
}

.v-slide-group--vertical .v-slide-group__content .v-btn {
  // border-radius: 10px !important;
}

.v-slide-group--vertical .v-slide-group-item--active {
  // background-color: #5d6ae3 !important;
  // border-radius: 10px !important;
  min-width: initial !important;
  color: white !important;
}

.v-tab-item--selected i {
  color: white !important;
}

.v-slide-group--vertical i {
  z-index: 10 !important;
}

.v-slide-group--vertical .v-tab__slider {
  top: 0;
  height: 100%;
  width: 100% !important;
  background-color: #5d6ae3;
  border-radius: 10px;
  transition: 0.4s;
}

.modal-body .v-btn--variant-elevated,
.modal-body .v-btn--variant-flat {
  background-color: var(--inner-background);
}

.modal-body .carditem {
  background-color: var(--inner-background) !important;
}

.fast_grey_background .v-btn {
  min-width: 64px !important;
}

.tabs-container {
  overflow-y: visible;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE 10+ */
}

.tabs-divider {
  margin-left: 12px;
  margin-right: 3px;
}

.expand-tabs {
  height: 50px;
  width: 50px;
  color: white;
  display: flex;
  justify-content: left;
  align-items: center;
  margin: auto;
  // background-color: black;
  margin-bottom: 10px;
  margin-left: 12px;
  border-radius: 6px;
  transition: 0.2s;
  cursor: pointer;
}

.expand-tabs:hover {
  box-shadow: 5px 5px 15px 5px #00000045 inset;
}

.expand-tabs-child {
  display: flex;
  width: 100%;
  align-items: center;
}

.expand-tabs-active {
  width: 200px;
}

.expand-tabs p {
  opacity: 0;
  position: absolute;
  transition-duration: unset;
  transition-delay: unset;
}

.expand-tabs-active p {
  opacity: 1;
  position: absolute;
  right: 30px;
  transition-duration: 0.2s;
  transition-delay: 0.1s;
  -moz-transform: translateX(-15px);
}

.expand-tabs-inactive p {
  opacity: 0;
  position: absolute;
}

.expand-tabs-active i {
  transform: rotate(180deg);
}

.tabs-container::-webkit-scrollbar {
  /* WebKit */
  width: 0px;
}

.tabs-inner-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
}

.tabicon,
.tabicon-logo {
  width: 55px;
  height: max-content;
  padding-bottom: 10px;
  padding-top: 10px;
  border-radius: 6px;
  margin-left: 12px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  user-select: none;
  margin-top: 14px;
  transition: all 0.2s ease-in;
  white-space: nowrap;
  flex-direction: column;
}

.tab-name {
  animation: fade-across;
  transition-delay: 0s !important;
  animation-duration: 0.5s;
  animation-delay: 0s;
}

.tab-name-active {
  position: relative;
  overflow: hidden;
}

@keyframes fade-across {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.tabicon-logo {
  padding-bottom: 18px;
  height: 75px;
  border-radius: 0px;
  padding-top: unset;
}

.tabiconlarge {
  width: 100px !important;
  height: 100px !important;
  border-radius: 6px;
  margin-right: 12px;
  user-select: none;
  margin-top: 0px;
  font-size: 70px !important;
  transition: all 0.2s ease-in;
}

.tabicon:hover {
  box-shadow: 5px 5px 15px 5px rgba(0, 0, 0, 0.27) inset;
  color: #ebecf0 !important;
}

.tabicon:active {
  box-shadow: 5px 5px 15px 5px rgba(0, 0, 0, 0.2) inset;
  transition: all 0.12s ease-in;
  color: #ebecf0 !important;
}

.tabiconsel {
  background: var(--primary-color);
  box-shadow: 5px 5px 15px 5px rgba(0, 0, 0, 0.15) inset;
  color: #ebecf0 !important;
}

.subtleshadow {
  overflow: hidden !important;
  border-radius: 7px !important;
  box-shadow: rgb(0 0 0 / 10%) 1px 1px 14px !important;
}

.nav-sub-items {
  border-top: 1px solid #d9d9d961;
  width: 65%;
  padding-top: 10px;
  margin-right: 10px;
}

.nav-sub-items ul {
  list-style-type: none;
  margin-left: -10px;
  display: flex;
  flex-direction: column;
  row-gap: 5px;
}

.nav-sub-items ul li {
  border-radius: 5px;
  padding: 5px;
  padding-left: 10px;
  padding-right: 12px;
  display: flex;
  flex-direction: unset;
  justify-content: space-between;
  align-items: center;
  pointer-events: auto;
}

.nav-sub-items ul li i {
  height: 10px;
  width: 5px;
  font-size: 20px;
}

.nav-sub-items ul li div {
  height: 8px;
  width: 8px;
  background-color: white;
  border-radius: 2px;
  opacity: 0;
  transition: 0.2s;
}

.nav-sub-items ul li:hover {
  background: #1a1a1f1c;
}

// .nav-sub-items ul li:hover div {
//   opacity: 1;
// }

.nav-sub-item-active {
  background: #1a1a1f4a;
}

.nav-sub-item-active div {
  opacity: 1 !important;
}

/*------ text field vuetify styling  ------*/

.channel-manager-dialog .v-overlay__content {
  max-width: 1400px !important;
}

.v-field--variant-filled .v-field__outline:before,
.v-field--variant-underlined .v-field__outline:before {
  top: 5px !important;
  opacity: 0 !important;
}

.v-field--variant-filled .v-field__outline:after,
.v-field--variant-underlined .v-field__outline:after {
  border-color: #5d6ae3 !important;
  top: 5px !important;
}

.v-text-field .v-field {
  border-radius: 5px;
}

.v-autocomplete .v-field .v-text-field__prefix,
.v-autocomplete .v-field .v-text-field__suffix,
.v-autocomplete .v-field .v-field__input,
.v-autocomplete .v-field.v-field {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-menu .v-overlay__content {
  max-width: 200px !important;
}

.v-menu .v-overlay__content.color-picker-menu {
  max-width: none !important;
  overflow: visible !important;
}

.v-btn.v-btn--density-default {
  height: 35px !important;
}

.refresh-button .v-btn.v-btn--density-default {
  height: 65px !important;
}

.refresh-button {
  width: 35px !important;
  min-width: unset !important;
  padding: 0px !important;
}

.text-info {
  margin: 0px !important;
  margin-bottom: 10px !important;
  margin-left: 0px !important;
  margin-right: 0px !important;
}

.overflow-input .v-input__control {
  overflow-x: hidden !important;
  overflow-y: visible !important;
}

.v-color-picker.v-sheet {
  box-shadow: unset !important;
}

@media (hover: hover) {
  .v-field--variant-filled:hover .v-field__overlay {
    opacity: calc(
      (0.09 + var(--v-hover-opacity)) * var(--v-theme-overlay-multiplier)
    ) !important;
  }
}

.appmain {
  left: 80px;
  display: flex;
  justify-content: center;
  align-self: flex-start;
  background: var(--v-backgroundColorDarker-base);
  width: calc(100vw - 100px);
  /* Height calculation: viewport - top margin (10px) - bottom bar (40px) - gap (5px) = 45px */
  height: calc(100vh - 55px);
  height: calc(100dvh - 55px);
  overflow-y: hidden;
  overflow-x: hidden;
  margin-top: 10px;
  margin-bottom: auto;
  border-radius: 10px;
  /* Width fallbacks for different browsers */
  width: -moz-available;
  width: -webkit-fill-available;
  width: fill-available;
  right: 0px;
  position: relative;
  margin-right: 90px;
  transform: unset !important;
  margin-left: 10px;
}

/* iOS: Extend appmain background downward to fill gap above bottom bar */
.appmain::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  transform: translateY(100%);
  background: var(--main-bg-color);
  z-index: -1;
  pointer-events: none;
}

/* Hide appmain::after in standalone mode - bottom bar handles safe area */
@media all and (display-mode: standalone) {
  .appmain::after {
    display: none;
  }
}

.appmainconsole {
  left: 80px;
  top: 50px;
  display: flex;
  justify-content: center;
  background: var(--v-backgroundColorDarker-base);
  width: calc(100vw - 80px);
  height: calc(100vh - 304px);
  height: calc(100dvh - 304px);
  overflow-y: hidden;
  overflow-x: hidden;
  border-radius: 10px 0px 0px 10px;
}

.console-bar {
  left: 80px;
  width: calc(100vw - 90px);
  display: flex;
  position: absolute;
  right: 10px;
  bottom: 40px;
  overflow: hidden;
  height: 200px;
  color: white;
  background: #0000007a;
  border-radius: 15px;
}

.console-bar-shifted {
  left: 180px;
  width: calc(100vw - 190px);
  display: flex;
  position: absolute;
  right: 10px;
  bottom: 40px;
  overflow: hidden;
  height: 200px;
  color: white;
  background: #0000007a;
  border-radius: 15px;
}

#logolarge {
  // width: 68px;
  // height: 67.85px;
  justify-content: center;
  align-items: center;
  display: flex;
  margin: 6px;
  border-radius: 5px;
  padding: 10px;
  cursor: unset !important;
  box-shadow: unset !important;
}

#logolarge img {
  transition: 0.2s;
}

#logosmall {
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  display: flex;
  margin: 5px;
  border-radius: 5px;
  padding: 5px;
  display: none;
  cursor: pointer;
}

#left-bar {
  top: 0;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  position: absolute;
  height: calc(100vh - 40px);
  height: calc(100dvh - 40px);
  height: calc(var(--vh, 1vh) * 100 - 40px);
  margin-left: 4px;
  z-index: 100;
  // width: 220px;
}

#left-bar2 {
  height: 100vh;
  height: 100dvh;
  height: calc(var(--vh, 1vh) * 100);
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 99999;
  background: var(--main-bg-color);
  transform: translateX(-80px);
  transition: all 0.3s;
  display: none;
  width: 80px;
  box-shadow: 0px 0px 8px 8px #0000001a;
}

.top-bar-buttons {
  height: 40px !important;
}

/* marquee stuff */

.marquee-inner-active {
  position: relative;
  margin-left: -100%;
  width: max-content !important;
  -moz-animation: marqueeInner 6s linear infinite forwards;
  -webkit-animation: marqueeInner 6s linear infinite forwards;
  animation: marqueeInner 6s linear infinite forwards;
}

// @-moz-keyframes marqueeOuter {
//   from {
//     margin-left: 100%;
//   }
//   to {
//     margin-left: 0%;
//   }
// }
// @-webkit-keyframes marqueeOuter {
//   from {
//     margin-left: 100%;
//   }
//   to {
//     margin-left: 0%;
//   }
// }
// @keyframes marqueeOuter {
//   from {
//     margin-left: 100%;
//   }
//   to {
//     margin-left: 0%;
//   }
// }

// @-moz-keyframes marqueeInner {
//   from {
//     left: 100%;
//   }
//   to {
//     left: 0%;
//   }
// }
// @-webkit-keyframes marqueeInner {
//   from {
//     left: 100%;
//   }
//   to {
//     left: 0%;
//   }
// }
// @keyframes marqueeInner {
//   from {
//     left: 100%;
//   }
//   to {
//     left: 0%;
//   }
// }
@-moz-keyframes marqueeOuter {
  0% {
    margin-left: 1%;
  }

  50% {
    margin-left: 0%;
  }

  50.0001% {
    margin-left: 100%;
  }

  100% {
    margin-left: 1%;
  }
}

@-webkit-keyframes marqueeOuter {
  0% {
    margin-left: 1%;
  }

  50% {
    margin-left: 0%;
  }

  50.0001% {
    margin-left: 100%;
  }

  100% {
    margin-left: 1%;
  }
}

@keyframes marqueeOuter {
  0% {
    margin-left: 1%;
  }

  50% {
    margin-left: 0%;
  }

  50.0001% {
    margin-left: 100%;
  }

  100% {
    margin-left: 1%;
  }
}

@-moz-keyframes marqueeInner {
  0% {
    left: 100%;
  }

  50% {
    left: 0%;
  }

  50.0001% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

@-webkit-keyframes marqueeInner {
  0% {
    left: 100%;
  }

  50% {
    left: 0%;
  }

  50.0001% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

@keyframes marqueeInner {
  0% {
    left: 100%;
  }

  50% {
    left: 0%;
  }

  50.0001% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.marquee-outer-active {
  display: block;
  position: absolute;
  width: auto !important;
  width: auto;
  left: 0;
  -moz-animation: marqueeOuter 6s linear infinite forwards;
  -webkit-animation: marqueeOuter 6s linear infinite forwards;
  animation: marqueeOuter 6s linear infinite forwards;
  overflow: visible !important;
}

/* progress bar styling */
.ingest-parent-progress .v-progress-linear__determinate {
  background-color: #5d6ae3;
  border-radius: 40px;
  max-width: 100% !important;
}

.bg-red {
  color: #db655c !important;
}

@media only screen and (max-width: 950px) {
  .page-container {
    padding-left: 10px;
    padding-right: 10px;
  }
}

/* iPad landscape and portrait specific styles (768px - 1024px) */
@media only screen and (min-width: 768px) and (max-width: 1024px) {
  .appmain {
    height: calc(100vh - 55px);
    height: calc(100dvh - 55px);
  }
}

/* iOS Browser mode (not standalone PWA) - use lvh for full height */
body.ios-browser .appmain {
  height: calc(100vh - 55px);
  height: calc(100lvh - 55px);
}

body.ios-browser #left-bar {
  height: calc(100vh - 40px);
  height: calc(100lvh - 40px);
}

body.ios-browser #left-bar2 {
  height: 100vh;
  height: 100lvh;
}

/* iOS Browser mode - mobile landscape */
@media screen and (max-width: 950px) and (orientation: landscape) {
  body.ios-browser .appmain {
    height: calc(100vh - 95px);
    height: calc(100lvh - 95px);
  }
}

/* iOS Browser mode - mobile portrait */
@media only screen and (max-width: 600px) {
  body.ios-browser .appmain {
    height: calc(100vh - 95px);
    height: calc(100lvh - 95px);
  }
}

/* Standalone PWA mode - full screen without browser chrome */
@media all and (display-mode: standalone) {
  /* Add top padding to account for iOS status bar */
  .appmain {
    height: calc(100vh - 55px - env(safe-area-inset-top));
    height: calc(100dvh - 55px - env(safe-area-inset-top));
    margin-top: calc(10px + env(safe-area-inset-top));
  }

  #left-bar {
    top: env(safe-area-inset-top);
    height: calc(100vh - 40px - env(safe-area-inset-top));
    height: calc(100dvh - 40px - env(safe-area-inset-top));
  }

  #left-bar2 {
    padding-top: env(safe-area-inset-top);
  }

  #top-bar {
    top: env(safe-area-inset-top);
  }
}

@media only screen and (max-width: 750px) {
  .expand-tabs {
    display: none;
  }
}

@media screen and (max-width: 950px) and (orientation: landscape) {
  #left-bar {
    display: none !important;
  }

  .appmain {
    width: calc(100vw) !important;
    /* Account for top bar (50px) + bottom bar (40px) + gap (5px) */
    height: calc(100vh - 95px);
    height: calc(100dvh - 95px);
    left: 0px;
    border-radius: 0px;
    transform: unset !important;
    margin-left: unset !important;
    margin-top: 50px;
    margin-bottom: auto;
  }

  #logosmall {
    display: block !important;
  }

  #top-bar {
    left: 0px !important;
    width: 100vw !important;
    user-select: all !important;
    z-index: 1000 !important;
    background-color: #242429;
    display: flex !important;
  }

  #left-bar2 {
    display: flex;
  }

  .CurrentMachine {
    font-size: 14px;
  }

  .HomeLogo {
    font-size: 14px;
  }
}

@media only screen and (max-width: 600px) {
  #left-bar {
    display: none !important;
  }

  .appmain {
    width: calc(100vw) !important;
    /* Account for top bar (50px) + bottom bar (40px) + gap (5px) */
    height: calc(100vh - 95px);
    height: calc(100dvh - 95px);
    left: 0px;
    border-radius: 0px;
    transform: unset !important;
    margin-left: unset !important;
    margin-top: 50px;
    margin-bottom: auto;
  }

  #logosmall {
    display: block !important;
  }

  #top-bar {
    left: 0px !important;
    width: 100vw !important;
    user-select: all !important;
    z-index: 1000 !important;
    background-color: #242429;
    display: flex !important;
  }

  #left-bar2 {
    display: flex;
  }

  .CurrentMachine {
    font-size: 14px;
  }

  .HomeLogo {
    font-size: 14px;
  }

  .player_manager {
    height: 180px !important;
  }
}

.connecting-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 9999999;
  background: var(--main-bg-color);
}

@media only screen and (max-width: 480px) {
  .condition-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .condition-item {
    width: 100% !important;
  }

  #bottom-bar {
    font-size: 12px !important;
  }

  // .display-name-styling {
  //   font-size: 12px;
  // }
}

.popup {
  position: fixed;
  pointer-events: none;
  /* Ensures the popup does not block other interactions */
  z-index: 99999;
}

/* Phone tabs overlay - uses proper viewport height for iOS */
.phone-tabs-overlay {
  background: #1c1c1f87;
  height: 100vh;
  height: 100dvh;
  height: calc(var(--vh, 1vh) * 100);
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
}

.phone-tabs-clickarea {
  left: 80px;
  height: 100vh;
  height: 100dvh;
  height: calc(var(--vh, 1vh) * 100);
  width: calc(100vw - 80px);
  position: absolute;
}

/* iOS PWA Status Bar Player Name */
.ios-status-bar-name {
  display: none; /* Hidden by default, shown only in iOS standalone */
}

@media all and (display-mode: standalone) {
  .ios-status-bar-name {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top);
    line-height: env(safe-area-inset-top);
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    z-index: 9999998; /* Below iOS warning overlay but above everything else */
    pointer-events: none;
    padding-top: 2px; /* Small nudge down to center in status bar */
  }
}

/* iOS PWA Warning Overlay */
.ios-pwa-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #1a1a1f;
  z-index: 9999999;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 40px 20px;
}

.ios-pwa-warning-content {
  max-width: 500px;
  text-align: center;
  color: white;
}

.ios-pwa-warning-logo {
  margin-bottom: 30px;
}

.ios-pwa-warning-logo img {
  width: 120px;
  height: 120px;
}

.ios-pwa-warning-hostname {
  font-size: 13px;
  opacity: 0.5;
  margin-bottom: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.ios-pwa-warning-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ios-pwa-warning-text {
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 20px;
}

.ios-pwa-warning-steps {
  text-align: left;
  font-size: 15px;
  line-height: 2;
  padding-left: 20px;
  margin-bottom: 40px;
  opacity: 0.9;
}

.ios-pwa-warning-steps li {
  margin-bottom: 8px;
}

.ios-pwa-warning-steps strong {
  color: var(--primary-color-light-text);
}

.ios-pwa-warning-cert-link {
  color: #64b5f6;
  text-decoration: underline;
  cursor: pointer;
}

.ios-pwa-warning-cert-link:hover {
  color: #90caf9;
}

.ios-pwa-warning-bypass {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.ios-pwa-warning-bypass-link {
  font-size: 13px;
  opacity: 0.5;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.2s;
}

.ios-pwa-warning-bypass-link:hover {
  opacity: 0.8;
}
</style>
